import { useEffect, useRef, useState } from "react";
import { pick, useT } from "../i18n/index.js";
import { Button } from "../ui/Button.js";
import { formatCompact } from "../ui/Counter.js";
import { Timer, useServerTick } from "../ui/Timer.js";
import { Panel } from "../ui/Panel.js";
import { itemById, type Resource } from "@zend/content";
import { GameIcon, ResourceIcon, RoleIcon, StatIcon } from "../assets/gameIcons.js";
import { Icon } from "../ui/Icon.js";
import { Stars } from "../units/shared.js";
import { expeditionsApi } from "../units/api.js";
import { useUnits } from "../units/state.js";
import type { ExpeditionStageView, RewardView } from "../units/types.js";
import { Dialog } from "../ui/Dialog.js";
import { StoryPanel } from "./StoryPanel.js";
import { ExpeditionBriefing } from "./ExpeditionBriefing.js";
import { navigate } from "../router.js";

function Reward({ r }: { r: RewardView }) {
  const t = useT();
  return (
    <span className="row" style={{ gap: 8, flexWrap: "wrap", fontSize: 12 }}>
      {Object.entries(r.resources).map(([k, v]) => (
        <span key={k} className="mono">
          <ResourceIcon resource={k as Resource} size={18} label={t(`res.${k}`)} /> {formatCompact(v)}
        </span>
      ))}
      {r.items.map((i) => (
        <span key={i.itemId} className="mono dim">
          <GameIcon id={i.itemId} size={18} /> {pick(itemById(i.itemId).name)} ×{i.qty}
        </span>
      ))}
      {r.credits > 0 ? <span className="mono" style={{ color: "var(--gold)" }}><ResourceIcon resource="CREDITS" label={t("store.credits")} /> {r.credits}</span> : null}
    </span>
  );
}

/** Dead Zone Expeditions (R36): chapters, stages with stars, attack/sweep, chests and story beats. */
export function ExpeditionsView() {
  const t = useT();
  const st = useUnits((s) => s.expeditions);
  const refresh = useUnits((s) => s.refreshExpeditions);
  const run = useUnits((s) => s.run);
  const setResult = useUnits((s) => s.setResult);
  const now = useServerTick();
  const [chapter, setChapter] = useState(1);
  const [storyView, setStoryView] = useState<{ stage: ExpeditionStageView; beforeAttack: boolean } | null>(null);
  const [attacking, setAttacking] = useState(false);
  const attackInFlight = useRef(false);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  useEffect(() => {
    if (!st) return;
    const firstOpen = st.chapters.find((c) => st.stages.some((s) => s.chapter === c.chapter && s.unlocked && s.stars < 3));
    if (firstOpen) setChapter(firstOpen.chapter);
  }, [st === null]);
  if (!st) return <p className="dim" style={{ padding: 16 }}>{t("common.loading")}</p>;
  const ch = st.chapters.find((c) => c.chapter === chapter)!;
  const stages = st.stages.filter((s) => s.chapter === chapter);

  const attack = async (stageId: string, squadSlot: number) => {
    if (attackInFlight.current) return;
    attackInFlight.current = true;
    setAttacking(true);
    try {
      const res = await run(() => expeditionsApi.attack(stageId, squadSlot));
      if (res) {
        setStoryView(null);
        setResult(null);
        navigate(`/app/battle/${res.battleId}`);
        void refresh();
      }
    } finally { attackInFlight.current = false; setAttacking(false); }
  };
  const sweep = async (stageId: string, times: number) => {
    const res = await run(() => expeditionsApi.sweep(stageId, times), t("expedition.toast.sweep"));
    if (res) void refresh();
  };
  const chest = async (index: number) => {
    const res = await run(() => expeditionsApi.chest(chapter, index), t("expedition.toast.chest"));
    if (res) void refresh();
  };

  return (
    <div className="page expeditions-view" data-testid="expeditions-view">
      <Panel title={t("expedition.title")}>
        <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
          <span className="badge gold" data-testid="stamina">
            <StatIcon stat="stamina" /> {st.stamina.current} / {st.stamina.cap}
          </span>
          {st.stamina.nextAt > now ? (
            <span className="hint">
              +1 <Timer finishesAt={st.stamina.nextAt} />
            </span>
          ) : null}
          <span className="badge">
            {t("expedition.sweeps")} {st.sweeps.freeLeft} · <GameIcon id="itm.sweep" label={pick(itemById("itm.sweep").name)} /> {st.sweeps.tickets}
          </span>
        </div>
        <div className="chapter-strip">
          {st.chapters.map((c) => {
            const unlocked = st.stages.some((s) => s.chapter === c.chapter && s.unlocked);
            return (
              <button key={c.chapter} type="button" className={`chapter ${c.chapter === chapter ? "active" : ""} ${unlocked ? "" : "locked"}`} onClick={() => unlocked && setChapter(c.chapter)} data-testid={`chapter-${c.chapter}`}>
                <div className="mono" style={{ fontSize: 11 }}>
                  {c.chapter}
                </div>
                <div className="ellipsis" style={{ fontSize: 11 }}>{pick(c.title)}</div>
                <div className="mono" style={{ fontSize: 10, color: "var(--gold)" }}>
                  <StatIcon stat="star" size={14} /> {c.stars}/36
                </div>
              </button>
            );
          })}
        </div>
      </Panel>

      <Panel title={`${chapter}. ${pick(ch.title)}`}>
        <div className="row" style={{ gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          {ch.chests.map((c) => (
            <Button key={c.index} variant={c.claimable ? "gold" : "ghost"} disabled={!c.claimable} style={{ minHeight: 32 }} onClick={() => void chest(c.index)} data-testid={`chest-${chapter}-${c.index}`}>
              <GameIcon id={`itm.chest_${["bronze", "silver", "gold"][c.index - 1] ?? "gold"}`} size={24} /> {c.stars}<StatIcon stat="star" size={14} /> {c.claimed ? <Icon name="check" /> : null}
            </Button>
          ))}
        </div>
        <div className="stage-grid">
          {stages.map((s) => (
            <div key={s.id} className={`stage ${s.unlocked ? "" : "locked"} ${s.boss ? "boss" : ""}`} data-testid={`stage-${s.id}`}>
              <div className="row" style={{ gap: 6 }}>
                <strong>
                  {s.chapter}-{s.stage}
                </strong>
                {s.boss ? <span className="badge warn">BOSS</span> : null}
                <span className="grow" />
                <span className="mono" style={{ color: "var(--gold)" }}>
                  <Stars stars={s.stars} max={3} size={13} />
                </span>
              </div>
              <div className="hint">
                Lv {s.packLevel} · <StatIcon stat="power" size={16} /> {formatCompact(s.power)} · <StatIcon stat="stamina" size={16} /> {t("expedition.stamina", { n: s.stamina })}
              </div>
              {s.story ? <StoryPanel key={s.story.panelId} story={s.story} onEnlarge={() => setStoryView({ stage: s, beforeAttack: false })} /> : null}
              <Reward r={s.clears === 0 ? s.firstClear : s.repeat} />
              <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                <Button variant="primary" style={{ minHeight: 44 }} disabled={attacking || !s.unlocked} onClick={() => setStoryView({ stage: s, beforeAttack: true })} data-testid={`attack-${s.id}`}>
                  <RoleIcon role="attacker" /> {pick({ de: "Einsatz prüfen", en: "Inspect mission" })}
                </Button>
                {s.sweepable ? (
                  <Button style={{ minHeight: 32 }} disabled={st.stamina.current < s.stamina || (st.sweeps.freeLeft === 0 && st.sweeps.tickets === 0)} onClick={() => void sweep(s.id, 1)} data-testid={`sweep-${s.id}`}>
                    <Icon name="skip" /> {t("expedition.sweep")}
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </Panel>
      {storyView ? <Dialog title={storyView.stage.story ? pick(storyView.stage.story.title) : `${pick(ch.title)} · ${storyView.stage.chapter}-${storyView.stage.stage}`} onClose={() => { if (!attacking) setStoryView(null); }} wide testId="expedition-story">
        {storyView.stage.story ? storyView.beforeAttack ? <details><summary>{pick({ de: "Einsatzgeschichte", en: "Mission story" })}</summary><StoryPanel key={storyView.stage.story.panelId} story={storyView.stage.story} /></details> : <><StoryPanel key={storyView.stage.story.panelId} story={storyView.stage.story} /><p className="hint">{t("story.illustration")}</p></> : null}
        {storyView.beforeAttack ? <><h3>{pick({ de: "Beute bei Sieg", en: "Victory loot" })}</h3><Reward r={storyView.stage.clears === 0 ? storyView.stage.firstClear : storyView.stage.repeat} /><ExpeditionBriefing key={storyView.stage.id} stage={storyView.stage} stamina={st.stamina.current} attacking={attacking} onAttack={(slot) => void attack(storyView.stage.id, slot)} /></> : null}
      </Dialog> : null}
    </div>
  );
}
